import React, { useState, useEffect, useMemo } from "react";
import { collection, query, where, getDocs, orderBy, addDoc, serverTimestamp, onSnapshot, updateDoc, doc } from "firebase/firestore";
import { db, auth } from "../../../firebase/firebase";
import { FiCalendar, FiMapPin, FiBriefcase, FiList, FiInfo, FiCheckCircle, FiGrid, FiLayout, FiBox } from "react-icons/fi";
import { IconWrapper } from "../../IconWrapper";
import { useAuth } from "../../../hooks/useAuth";
import { useColorMode } from "@chakra-ui/react";
import { getThemeClasses } from "../../../utils/themeUtils";
import { Booking, Floor, FloorAssignment, Seat } from "./types";
import { useFloorFiltering, useAvailableSeats } from "./hooks";
import { DateSelector } from "./DateSelector";
import { FloorList } from "./FloorList";
import { SeatGrid } from "./SeatGridView";
import { MyBookingsList } from "./MyBookingsList";
import { toLocalDateString } from "../../../utils/dateUtils";
import { showToast } from "../../../utils/toastUtils";

interface UserBookingProps {
  currentOrgId?: string;
}

const UserBooking: React.FC<UserBookingProps> = ({ currentOrgId: propOrgId }) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  const theme = getThemeClasses(isDark);
  const { orgId } = useAuth();
  const user = auth.currentUser;
  const currentOrgId = propOrgId || orgId || "Xyi09suiej75RuHayi";

  const [viewMode, setViewMode] = useState<'book' | 'my-bookings'>('book');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [floors, setFloors] = useState<Floor[]>([]);
  const [floorAssignments, setFloorAssignments] = useState<FloorAssignment[]>([]);
  const [bookingsOnDate, setBookingsOnDate] = useState<Booking[]>([]);
  const [myBookings, setMyBookings] = useState<Booking[]>([]);
  const [selectedFloorId, setSelectedFloorId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [bookingInProgress, setBookingInProgress] = useState(false);

  // Use custom hooks
  const { availableFloors } = useFloorFiltering(floors, floorAssignments, selectedDate);
  const { getAvailableSeatsForFloor, canBookFloor } = useAvailableSeats(floors, floorAssignments, bookingsOnDate, selectedDate);

  // Fetch floors
  useEffect(() => {
    if (!currentOrgId) return;
    const floorsUnsub = onSnapshot(collection(db, `organization/${currentOrgId}/floors`), (snap) => {
      const data: Floor[] = [];
      snap.docs.forEach(d => {
        const floorData = d.data();
        data.push({
          id: d.id,
          name: floorData.name,
          seats: floorData.seats || [],
          isVisible: floorData.isVisible ?? true,
          isActive: floorData.isActive ?? true,
          gridRows: floorData.gridRows,
          gridCols: floorData.gridCols
        });
      });
      setFloors(data);
      setLoading(false);
    });
    return () => floorsUnsub();
  }, [currentOrgId]);

  // Fetch floor assignments
  useEffect(() => {
    if (!currentOrgId) return;
    const assignUnsub = onSnapshot(collection(db, `organization/${currentOrgId}/floor_assignments`), (snap) => {
      const data: FloorAssignment[] = [];
      snap.docs.forEach(d => {
        const assignData = d.data();
        data.push({
          id: d.id,
          date: assignData.date,
          floorId: assignData.floorId,
          availableSeats: assignData.availableSeats || 0,
          canView: assignData.canView ?? true,
          canBook: assignData.canBook ?? true
        });
      });
      setFloorAssignments(data);
    });
    return () => assignUnsub();
  }, [currentOrgId]);

  // Auto-select first available floor when date changes
  useEffect(() => {
    if (availableFloors.length > 0 && !availableFloors.find(f => f.id === selectedFloorId)) {
      setSelectedFloorId(availableFloors[0].id);
    }
  }, [availableFloors, selectedFloorId]);

  // Auto-select valid date logic removed to allow seeing empty state
  // useEffect(() => { ... })

  // Fetch bookings for selected date
  useEffect(() => {
    if (!currentOrgId) return;
    const dateStr = toLocalDateString(selectedDate);
    const q = query(
      collection(db, `organization/${currentOrgId}/bookings`),
      where("date", "==", dateStr),
      where("status", "in", ["active", "pending"])
    );
    
    const unsub = onSnapshot(q, (snap) => {
      const data: Booking[] = [];
      snap.docs.forEach(d => {
        data.push({ id: d.id, ...d.data() } as Booking);
      });
      setBookingsOnDate(data);
    });
    return () => unsub();
  }, [currentOrgId, selectedDate]);

  // Fetch user's bookings
  useEffect(() => {
    if (!currentOrgId || !user) {
      console.log('MyBookings: Missing orgId or user', { currentOrgId, userId: user?.uid });
      setMyBookings([]);
      return;
    }
    
    console.log('MyBookings: Setting up listener for user:', user.uid);
    
    const q = query(
      collection(db, `organization/${currentOrgId}/bookings`),
      where("userId", "==", user.uid),
      where("status", "in", ["active", "pending"])
    );
    
    const unsub = onSnapshot(
      q, 
      (snap) => {
        const data: Booking[] = [];
        snap.docs.forEach(d => {
          data.push({ id: d.id, ...d.data() } as Booking);
        });
        // Sort by date in JavaScript instead of Firestore to avoid composite index requirement
        data.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        console.log('MyBookings: Received bookings:', data.length, data);
        setMyBookings(data);
      },
      (error) => {
        console.error('MyBookings: Error fetching bookings:', error);
        showToast({ 
          title: "Error loading bookings", 
          description: error.message,
          status: "error" 
        });
      }
    );
    return () => unsub();
  }, [currentOrgId, user]);

  const handleBookSeat = async (floor: Floor, seat: Seat) => {
    if (!user) {
      showToast({ title: "Please sign in to book", status: "warning" });
      return;
    }

    // Check if booking is allowed for this floor
    if (!canBookFloor(floor.id)) {
      showToast({ 
        title: "Booking not allowed", 
        description: "This floor is in view-only mode",
        status: "warning" 
      });
      return;
    }

    const dateStr = toLocalDateString(selectedDate);
    
    // Check if user already has a booking for this date
    const existingBooking = myBookings.find(b => b.date === dateStr && (b.status === "active" || b.status === "pending"));
    if (existingBooking) {
      showToast({ title: "You already have a booking for this date", status: "warning" });
      return;
    }

    setBookingInProgress(true);
    try {
      await addDoc(collection(db, `organization/${currentOrgId}/bookings`), {
        seatId: seat.id,
        seatName: seat.name,
        floorId: floor.id,
        floorName: floor.name,
        date: dateStr,
        userId: user.uid,
        status: "pending",
        createdAt: serverTimestamp()
      });
      
      showToast({ 
        title: "Booking request submitted", 
        description: `${seat.name} on ${floor.name} - Pending approval`,
        status: "success" 
      });
    } catch (error) {
      console.error("Booking error:", error);
      showToast({ title: "Failed to book seat", status: "error" });
    } finally {
      setBookingInProgress(false);
    }
  };

  const handleCancelBooking = async (bookingId: string) => {
    if (!window.confirm("Cancel this booking?")) return;
    
    try {
      await updateDoc(doc(db, `organization/${currentOrgId}/bookings`, bookingId), {
        status: "cancelled"
      });
      showToast({ title: "Booking cancelled", status: "info" });
    } catch (error) {
      showToast({ title: "Failed to cancel booking", status: "error" });
    }
  };

  const selectedFloor = floors.find(f => f.id === selectedFloorId);
  const availableSeatsForFloor = selectedFloor ? getAvailableSeatsForFloor(selectedFloor.id) : [];

  return (
    <div className={`min-h-screen ${theme.background} ${theme.text}`}>
      {/* Premium Header */}
      <div className={`${theme.headerGradient} px-6 py-4 mb-6 rounded-md border ${theme.border}`}>
        {/* Dark mode decorative overlays */}
        {isDark && (
          <>
            <span className={theme.headerOverlayGlow} />
            <span className={theme.headerOverlaySweep} />
            <span className={theme.headerOverlayTopLine} />
            <span className={theme.headerOverlayBottomGlow} />
          </>
        )}
        
        <div className="relative z-10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-xl ${isDark ? 'bg-gradient-to-br from-[#FBD282]/20 to-[#B59143]/20 ring-1 ring-[#B59143]/30' : 'bg-gradient-to-br from-[#FBD282] to-[#B59143]'} shadow-lg`}>
              <IconWrapper icon={FiBriefcase} size={22} className={isDark ? 'text-[#FBD282]' : 'text-white'} />
            </div>
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Booking Management
              </h1>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Manage floors, seats, and user assignments
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6  mb-6">
        <div className="flex gap-8">
          <button
            onClick={() => setViewMode('book')}
            className={`
              flex items-center gap-2 pb-3 px-1 text-sm font-semibold transition-all relative
              ${viewMode === 'book'
                ? isDark ? 'text-[#B59143]' : 'text-[#B59143]'
                : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-800'
              }
            `}
          >
            <IconWrapper icon={FiGrid} size={16} />
            <span>Book Seat</span>
            {viewMode === 'book' && (
              <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#B59143] rounded-t-full" />
            )}
          </button>
          
          <button
            onClick={() => setViewMode('my-bookings')}
            className={`
              flex items-center gap-2 pb-3 px-1 text-sm font-semibold transition-all relative
              ${viewMode === 'my-bookings'
                ? isDark ? 'text-[#B59143]' : 'text-[#B59143]'
                : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-800'
              }
            `}
          >
            <IconWrapper icon={FiList} size={16} />
            <span>My Bookings</span>
            {viewMode === 'my-bookings' && (
              <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#B59143] rounded-t-full" />
            )}
          </button>
        </div>
      </div>    {/* Content Area */}
        <div className={`${theme.card} rounded-xl ${isDark ? 'border border-[#2E2F33]' : 'border border-gray-200'} shadow-sm p-6`}>
          {viewMode === 'book' ? (
            <div className="flex flex-col gap-1">
              {/* Date Selector */}
              <DateSelector 
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
                theme={theme}
                floorAssignments={floorAssignments}
              />

              <div className="flex flex-col h-full min-h-0 gap-2">
                {/* Top: Floor Selection Tabs - Only show if floors exist */}
                {availableFloors.length > 0 && (
                  <div className="flex items-center gap-3 overflow-x-auto no-scrollbar py-1 px-1">
                    {availableFloors.map(floor => (
                      <button
                        key={floor.id}
                        onClick={() => setSelectedFloorId(floor.id)}
                        className={`
                          px-5 py-2.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap shadow-sm
                          ${selectedFloorId === floor.id
                            ? isDark
                              ? 'bg-white text-black shadow-lg shadow-white/10 scale-105'
                              : 'bg-[#0f1115] text-white shadow-md shadow-black/20 scale-105'
                            : isDark
                              ? 'bg-[#1e2126] text-gray-400 hover:bg-[#2A3441] hover:text-gray-200 border border-[#2E2F33]'
                              : 'bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 border border-gray-200'
                          }
                        `}
                      >
                        {floor.name}
                      </button>
                    ))}
                  </div>
                )}

                {/* Content Row: Grid + Right Sidebar */}
                {availableFloors.length > 0 ? (
                  <div className="flex flex-1 gap-4 overflow-hidden">
                    {/* ... (Existing Grid Code) ... */}
                    <main className={`flex-1 ${isDark ? 'bg-[#1A1C1F] border-[#2E2F33]' : 'bg-gray-50 border-gray-200'} rounded-2xl border shadow-inner flex flex-col overflow-hidden relative h-[600px]`}>
                       <div className="flex-1 overflow-hidden relative">
                       <SeatGrid
                            seats={(selectedFloor?.seats || []).slice(0, (() => {
                               const dateStr = toLocalDateString(selectedDate);
                               const assignment = floorAssignments.find(a => a.floorId === selectedFloor?.id && a.date === dateStr);
                               return assignment ? assignment.availableSeats : (selectedFloor?.seats?.length || 0);
                            })())}
                            allBookings={bookingsOnDate}
                            myUserId={auth.currentUser?.uid}
                            selectedDate={selectedDate}
                            loading={loading}
                            bookingInProgress={bookingInProgress}
                            onSeatBook={(seat) => selectedFloor && handleBookSeat(selectedFloor, seat)}
                            theme={theme}
                            floor={selectedFloor}
                            canBook={selectedFloor ? canBookFloor(selectedFloor.id) : false}
                          />
                       </div>
                    </main>
 
                    {/* Right: Booking Summary Sidebar */}
                    <aside className="w-72 flex flex-col gap-4 min-w-[280px]">
                      {/* Date Summary Card */}
                      <div className={`${isDark ? 'bg-[#1e2126] border-[#2E2F33]' : 'bg-white border-gray-100'} border rounded-xl p-4 shadow-sm`}>
                        <div className="flex items-center gap-2 mb-3">
                           <div className={`p-1.5 rounded-md ${isDark ? 'bg-[#B59143]/20' : 'bg-[#fff8e6]'}`}>
                              <IconWrapper icon={FiCalendar} size={14} className="text-[#B59143]" />
                           </div>
                           <span className={`text-[10px] font-bold uppercase tracking-widest ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Selected Date</span>
                        </div>
                        <div className="flex flex-col items-center py-2">
                           <div className={`text-3xl font-black ${theme.text} mb-1 tracking-tighter`}>
                              {selectedDate.getDate()}
                           </div>
                           <div className={`text-xs font-bold ${isDark ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                              {selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                           </div>
                           <div className={`text-[10px] font-semibold mt-1 text-[#B59143]`}>
                              {selectedDate.toLocaleDateString('en-US', { weekday: 'long' })}
                           </div>
                        </div>
                      </div>
 
                      {/* User's Booking Status Card (If booked on this floor) */}
                      {selectedFloor && (() => {
                        const dateStr = toLocalDateString(selectedDate);
                        const userBooking = myBookings.find(b => b.floorId === selectedFloor.id && b.date === dateStr && (b.status === 'active' || b.status === 'pending'));
                        
                        if (userBooking) {
                          return (
                            <div className={`${isDark ? 'bg-blue-900/10 border-blue-500/30' : 'bg-blue-50 border-blue-200'} border rounded-2xl p-6 shadow-sm relative overflow-hidden group`}>
                               
                               <div className="relative z-10">
                                  <div className="flex items-center justify-between mb-1">
                                     <div className={`text-[10px] font-bold uppercase tracking-widest ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>Your Seat</div>
                                     <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>
                                        <IconWrapper icon={FiCheckCircle} size={16} />
                                     </div>
                                  </div>
                                  <div className="flex items-center gap-3 mb-1">
                                  <div className={`text-2xl font-black ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
                                    {userBooking.seatName}
                                  </div>
                                  <div className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide ${
                                    userBooking.status === 'active' 
                                      ? 'bg-green-500/10 text-green-600 dark:text-green-400' 
                                      : 'bg-orange-500/10 text-orange-600 dark:text-orange-400'
                                  }`}>
                                     <div className={`w-2 h-2 rounded-full ${userBooking.status === 'active' ? 'bg-green-500' : 'bg-orange-500'} animate-pulse`}></div>
                                     {userBooking.status === 'active' ? 'Confirmed' : 'Pending'}
                                  </div>
                                  </div>
                               </div>
                            </div>
                          );
                        }
                        return null;
                      })()}
 
                      {/* Floor Stats Card */}
                      {selectedFloor && (() => {
                         // Get the actual capacity limit set by manager
                         const dateStr = toLocalDateString(selectedDate);
                         const assignment = floorAssignments.find(a => a.floorId === selectedFloor.id && a.date === dateStr);
                         const totalCapacity = assignment ? assignment.availableSeats : (selectedFloor.seats?.length || 0);
                         const availableCount = availableSeatsForFloor.length;
                         
                         // Ensure booked count doesn't look negative if logic drifts
                         const bookedCount = Math.max(0, totalCapacity - availableCount);
 
                         return (
                          <div className={`${isDark ? 'bg-[#1e2126] border-[#2E2F33]' : 'bg-white border-gray-100'} border rounded-xl p-4 shadow-sm`}>
                            <div className="flex items-center gap-2 mb-4">
                               <div className={`p-1.5 rounded-md ${isDark ? 'bg-blue-500/20' : 'bg-blue-50'}`}>
                                  <IconWrapper icon={FiMapPin} size={14} className="text-blue-500" />
                               </div>
                               <span className={`text-[10px] font-bold uppercase tracking-widest ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Availability</span>
                            </div>
                            
                            <div className="space-y-4">
                               <div className="flex justify-between items-center text-xs">
                                  <span className={`${theme.textMuted}`}>Total Units</span>
                                  <span className={`font-bold ${theme.text} text-sm`}>{totalCapacity}</span>
                               </div>
                               <div className="flex justify-between items-center text-xs">
                                  <span className={`${theme.textMuted}`}>Available</span>
                                  <span className="font-bold text-green-500 text-sm">{availableCount}</span>
                               </div>
                               
                               {/* Progress Bar */}
                               <div>
                                  <div className={`h-1.5 w-full rounded-full ${isDark ? 'bg-gray-800' : 'bg-gray-100'} overflow-hidden`}>
                                     <div 
                                       className="h-full rounded-full bg-green-500" 
                                       style={{ width: `${totalCapacity > 0 ? (availableCount / totalCapacity) * 100 : 0}%` }}
                                     />
                                  </div>
                                  <div className="flex justify-end mt-1">
                                     <span className="text-[9px] text-gray-400 font-medium">
                                        {totalCapacity > 0 ? Math.round((bookedCount / totalCapacity) * 100) : 0}% Occupied
                                     </span>
                                  </div>
                               </div>
                            </div>
                          </div>
                          );
                       })()}
                    </aside>
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center p-12 animate-in fade-in zoom-in duration-300">
                     <div className={`w-24 h-24 rounded-full flex items-center justify-center mb-6 shadow-md ${isDark ? 'bg-gradient-to-br from-[#1e2126] to-[#14161B] shadow-black/50 border border-[#2E2F33]' : 'bg-white shadow-gray-200 border border-gray-100'}`}>
                        <IconWrapper icon={FiBox} size={24} className="text-[#B59143] w-12 h-12" />
                     </div>
                     <h3 className={`text-2xl font-black mb-2 ${theme.text}`}>No Floors Opened</h3>
                     <p className={`text-sm text-center max-w-md ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        The administrator has not opened any floor bookings for <span className="font-bold text-[#B59143]">{selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}</span>
                     </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="max-w-6xl mx-auto w-full">
              <div className="mb-6">
                <h2 className={`text-2xl font-bold mb-1 ${theme.text}`}>My Bookings</h2>
                <p className={`text-sm ${theme.textMuted}`}>Manage your upcoming seat reservations</p>
              </div>
              <MyBookingsList
                bookings={myBookings}
                onCancelBooking={handleCancelBooking}
                theme={theme}
              />
            </div>
          )}
        </div>
      </div>
   
  );
};

export default UserBooking;
